import { useQuery } from '@tanstack/react-query'
import { SearchIcon, ChevronDownIcon, Loader2Icon, Building2Icon } from 'lucide-react'
import { useState, useEffect, useRef, useId } from 'react'

import { useDebounce } from '../hooks'
import { organizationApi, type Organization } from '../services/organizationApi'

const ORG_TYPE_LABEL: Record<Organization['type'], string> = {
  system: 'Sistema',
  cloud_provider: 'Cloud Provider',
  reseller: 'Reseller',
  end_customer: 'Cliente Final',
}

export type OrgSearchSelectProps = {
  value: Organization | null
  onChange: (org: Organization | null) => void
  /**
   * Rótulo de uma opção "sem organização específica" no topo da lista (ex.: "Organização
   * ativa"). Selecioná-la chama `onChange(null)`. Ausente = sem essa opção (campo obrigatório).
   */
  activeLabel?: string
  placeholder?: string
  disabled?: boolean
  /** Mínimo de caracteres para disparar a busca. Default 2. */
  minChars?: number
  className?: string
  'aria-label'?: string
}

/**
 * Combobox de busca de organização (via `organizationApi.searchOrganizations`), com debounce,
 * teclado (↑/↓/Enter/Esc), fechar-ao-clicar-fora e ARIA de combobox. Retorna a `Organization`
 * completa — o chamador usa o que precisar (`id`/`name` no filtro; `type`/`status` no modal).
 *
 * Unifica as variantes que existiam soltas (filtro do RolesPage e dropdown do CreateRoleModal).
 * Não cobre a troca de organização ATIVA — isso é o `OrgSwitcher` (navega por URL).
 */
export function OrgSearchSelect({
  value,
  onChange,
  activeLabel,
  placeholder = 'Buscar organização...',
  disabled = false,
  minChars = 2,
  className = '',
  'aria-label': ariaLabel = 'Organizações',
}: OrgSearchSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const debouncedSearch = useDebounce(search, 300)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const {
    data: orgs = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['organizations', 'org-search', debouncedSearch, minChars],
    queryFn: () => organizationApi.searchOrganizations({ search: debouncedSearch, limit: 10 }),
    enabled: open && debouncedSearch.length >= minChars,
  })

  // `stale` (síncrono): o debounce ainda não alcançou o input → os resultados atuais podem não
  // corresponder ao texto. Guarda a lista por aqui, NÃO pelo loading (que dependeria de minChars
  // e deixaria itens antigos clicáveis ao apagar). `searching` é só o spinner.
  const stale = open && search !== debouncedSearch
  const validSearch = search.length >= minChars
  // resultados só aparecem com uma busca válida cujo debounce já alcançou e não está carregando.
  // Cobre o apagar abaixo de minChars: sem `validSearch`, a query desabilitada manteria o `orgs`
  // da busca anterior e ele reapareceria stale.
  const showResults = validSearch && !stale && !isLoading
  const searching = validSearch && (isLoading || stale)

  // opções navegáveis, na ordem em que aparecem: a "ativa" (se houver) e as orgs mostráveis
  const options: (Organization | null)[] = [
    ...(activeLabel ? [null] : []),
    ...(showResults ? orgs : []),
  ]

  const reset = () => {
    setOpen(false)
    setSearch('')
    setActiveIndex(-1)
  }
  const close = () => {
    reset()
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) reset()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  // o índice destacado não pode passar do fim quando a lista encolhe
  useEffect(() => {
    setActiveIndex((i) => (i >= options.length ? options.length - 1 : i))
  }, [options.length])

  // mantém o item destacado por teclado dentro da área visível da lista
  const optionRefs = useRef<(HTMLLIElement | null)[]>([])
  useEffect(() => {
    if (activeIndex >= 0) optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const pick = (org: Organization | null) => {
    onChange(org)
    close()
  }

  // no div raiz (não só no input): cobre o foco em qualquer parte do componente, e o
  // stopPropagation impede que Esc/Enter cheguem a um <form> ou modal do consumidor —
  // o componente é do shared e não pode fechar o modal ao redor nem submetê-lo por implicação.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      close()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, options.length - 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      if (activeIndex >= 0 && activeIndex < options.length) pick(options[activeIndex])
    }
  }

  const triggerLabel = value ? value.name : (activeLabel ?? placeholder)
  const optId = (i: number) => `${listId}-opt-${i}`

  return (
    <div ref={rootRef} onKeyDown={onKeyDown} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 px-4 py-2 text-left focus:ring-2 focus:ring-red-500 focus:outline-none disabled:bg-gray-100"
      >
        <span
          className={`flex items-center gap-2 truncate text-sm ${value ? '' : 'text-gray-500'}`}
        >
          <Building2Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <span className="truncate">{triggerLabel}</span>
        </span>
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="relative border-b border-gray-100 p-2">
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-activedescendant={activeIndex >= 0 ? optId(activeIndex) : undefined}
              placeholder={`${placeholder}${minChars > 1 ? ` (${minChars}+ letras)` : ''}`}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setActiveIndex(-1)
              }}
              className="w-full rounded border border-gray-300 px-3 py-1.5 pr-8 text-sm focus:ring-1 focus:ring-red-500 focus:outline-none"
            />
            <SearchIcon className="absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 text-gray-400" />
          </div>
          {/* preventDefault no mousedown mantém o foco no input ao clicar em qualquer item
              (inclusive os de estado, não-focáveis) — senão o foco iria para o body e o Esc
              deixaria de fechar. O onClick das opções ainda dispara. */}
          <ul
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            onMouseDown={(e) => e.preventDefault()}
            className="max-h-56 overflow-y-auto py-1 text-sm"
          >
            {options.map((org, i) => {
              const selected = org ? value?.id === org.id : !value
              return (
                <li
                  key={org ? org.id : '__active__'}
                  ref={(el) => {
                    optionRefs.current[i] = el
                  }}
                  id={optId(i)}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => pick(org)}
                  className={`flex cursor-pointer flex-col px-3 py-1.5 ${
                    i === activeIndex ? 'bg-gray-100' : ''
                  } ${selected ? 'bg-red-50 text-red-700' : ''}`}
                >
                  {org ? (
                    <>
                      <span className="truncate font-medium">{org.name}</span>
                      <span className="text-xs text-gray-500">
                        {ORG_TYPE_LABEL[org.type] ?? org.type} • {org.status}
                      </span>
                    </>
                  ) : (
                    <span className="font-medium">{activeLabel}</span>
                  )}
                </li>
              )
            })}
            {searching && (
              <li role="presentation" className="flex items-center gap-2 px-3 py-2 text-gray-500">
                <Loader2Icon className="h-3.5 w-3.5 animate-spin" /> Buscando…
              </li>
            )}
            {isError && (
              <li role="presentation" className="px-3 py-2 text-red-600">
                Falha ao buscar organizações
              </li>
            )}
            {showResults && !isError && orgs.length === 0 && (
              <li role="presentation" className="px-3 py-2 text-gray-400">
                Nenhuma organização encontrada
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
